import React from "react";
import { Check } from "lucide-react";
import TipCallout from "./TipCallout";
import {
  SCROLL_OFFSET,
  ARTICLE_COLUMN,
  H2,
  BODY,
  BODY_SM,
  CARD_TITLE,
  STACK_GAP,
} from "./sections";

const REASONS = [
  {
    title: "Built for dictating prompts, not short commands",
    body: "Built-in voice typing is designed for general dictation or short commands. It is not fine-tuned for the way people actually talk through a detailed prompt — context up front, a task in the middle, and format instructions on the end. Zero Typing AI is built around that specific use case.",
  },
  {
    title: "Cleaner punctuation and formatting",
    body: "Zero Typing AI applies AI-powered cleanup to what you say, adding punctuation and structure so a spoken prompt reads like something you typed carefully, rather than a raw transcript.",
  },
  {
    title: "Better at understanding natural speech",
    body: "Because it is fine-tuned specifically for voice typing, it follows the pauses, corrections, and rephrasing that happen naturally when you talk, instead of transcribing every false start word for word.",
  },
  {
    title: "One tool across every app",
    body: "Built-in voice typing only works inside the app that built it. Zero Typing AI works as an overlay across all of them, so you use the same tool everywhere. It also works well beyond AI chatbots, including voice typing for Google Docs, Gmail, and Slack.",
  },
  {
    title: "Save your own prompt structures",
    body: "Zero Typing AI lets you save a custom prompt structure, so a tone, format, or set of instructions you often use gets applied automatically every time you dictate. Built-in voice typing tools do not offer this.",
  },
];

const WhyZeroTypingSection = () => {
  return (
    <section
      id="why-zero-typing"
      style={{ scrollMarginTop: `${SCROLL_OFFSET}px` }}
      className={ARTICLE_COLUMN}
    >
      <h2 className={H2}>
        Why Use Zero Typing AI Instead Of Built-In Voice Typing
      </h2>

      <p className={`${STACK_GAP} ${BODY}`}>
        Most AI chatbots already include some form of voice typing or voice
        command feature built directly into the app. So it is a fair question to
        ask why add another tool on top of that. The difference comes down to
        what built-in voice typing is designed for, versus what Zero Typing AI
        is built for.
      </p>

      <div className="pt-[30.05px]">
        <div className="flex flex-col gap-[17.17px]">
          {REASONS.map(({ title, body }) => (
            <article
              key={title}
              className="flex items-start gap-[12.88px] rounded-[17.17px] border-[1px] border-[#3AB1F633] bg-[#EBF7FE] p-[16px] sm:p-[21.46px]"
            >
              {/* Filled tick box, as in the design */}
              <span className="mt-[4px] flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-[5px] bg-[#3AB1F6] text-white">
                <Check className="h-[11px] w-[11px]" strokeWidth={3} />
              </span>

              <div className="min-w-0">
                <h3 className={CARD_TITLE}>{title}</h3>
                <p className={`mt-[4px] ${BODY_SM}`}>{body}</p>
              </div>
            </article>
          ))}
        </div>
      </div>

      <TipCallout>
        Dictate the same prompt into Claude using its own built-in voice typing,
        then again using Zero Typing AI, and compare the two results side by
        side.
      </TipCallout>
    </section>
  );
};

export default WhyZeroTypingSection;
